/**
 * Group email templates the same way Client Email does: one row per EN/ES pair
 * (template_code with -english / -spanish / -en / -es stripped to get the "base").
 */

export const CLIENT_LETTERS_CATEGORY = 'Client Letters';

/** Email templates for school / LEA correspondence (Client Email page, School section). */
export const SCHOOL_EMAIL_CATEGORY = 'School';

/** Same category as Monthly Contact list page (Settings → Email Templates). */
export const MONTHLY_CONTACT_EMAIL_CATEGORY = 'Monthly Contact';

export function normalizeTemplateLanguage(lang) {
  const u = (lang || '').trim().toUpperCase();
  if (u === 'ES' || u === 'SPA' || u === 'SPANISH') return 'ES';
  if (u === 'EN' || u === 'ENG' || u === 'ENGLISH') return 'EN';
  return u || 'EN';
}

/** Prefer name/code when `language` is unset so EN/ES pairs are not both treated as EN. */
export function inferMonthlyTemplateRowLanguage(row) {
  if (!row) return 'EN';
  const name = String(row.display_name || row.displayName || '');
  if (/\(\s*ES\s*\)\s*$/i.test(name) || /\(\s*SPA\s*\)\s*$/i.test(name) || /\(\s*Spanish\s*\)\s*$/i.test(name)) {
    return 'ES';
  }
  if (/\(\s*EN\s*\)\s*$/i.test(name) || /\(\s*ENG\s*\)\s*$/i.test(name) || /\(\s*English\s*\)\s*$/i.test(name)) {
    return 'EN';
  }
  const code = String(row.template_code || '');
  if (/-spanish$/i.test(code) || /-es$/i.test(code) || /_spanish$/i.test(code) || /_es$/i.test(code)) return 'ES';
  if (/-english$/i.test(code) || /-en$/i.test(code) || /_english$/i.test(code) || /_en$/i.test(code)) return 'EN';
  const raw = String(row.language ?? '').trim();
  if (raw) return normalizeTemplateLanguage(raw);
  return 'EN';
}

/** Match deep links / automation rules to a group (case-insensitive on normalized base). */
export function normalizeTemplateBaseCode(code) {
  let s = String(code || '').trim();
  const suffixes = [
    /-english$/i,
    /-spanish$/i,
    /-en$/i,
    /-es$/i,
    /_english$/i,
    /_spanish$/i,
    /_en$/i,
    /_es$/i,
  ];
  for (const re of suffixes) {
    s = s.replace(re, '');
  }
  return s.toLowerCase();
}

/**
 * Monthly Contact (and similar): group rows that share the same human-facing message name,
 * even when template_code does not follow -english/-spanish (e.g. "MC-…" pairs only differ in display_name).
 */
export function normalizeMonthlyTemplateGroupKeyFromRow(row) {
  const label = stripParentheticalLanguageSuffix(row?.display_name || row?.displayName || row?.template_code || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  return label || normalizeTemplateBaseCode(row?.template_code || '');
}

/**
 * @returns {Array<{ groupKey: string, displayName: string, rows: Array<object> }>}
 */
export function groupMonthlyContactEmailSelectOptions(templates) {
  const m = new Map();
  for (const t of templates || []) {
    const key = normalizeMonthlyTemplateGroupKeyFromRow(t);
    if (!m.has(key)) {
      m.set(key, {
        groupKey: key,
        displayName: stripParentheticalLanguageSuffix(t.display_name || t.displayName || t.template_code || '').trim() || key,
        rows: [],
      });
    }
    m.get(key).rows.push(t);
  }
  return Array.from(m.values()).sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '', undefined, { sensitivity: 'base' }));
}

export function monthlyGroupHasSpanishTemplate(group) {
  const rows = group?.rows || [];
  return rows.some((r) => inferMonthlyTemplateRowLanguage(r) === 'ES');
}

/**
 * @param {Array<{ template_code?: string, display_name?: string, language?: string }>} templates
 * @returns {Array<{ baseCode: string, displayName: string, codes: Array<{ template_code: string, language: string }> }>}
 */
export function groupClientLetterTemplates(templates) {
  const byBase = new Map();
  for (const t of templates) {
    const baseKey = normalizeTemplateBaseCode(t.template_code || '');
    if (!baseKey) continue;
    if (!byBase.has(baseKey)) {
      byBase.set(baseKey, { baseCode: baseKey, displayName: t.display_name || t.template_code, codes: [] });
    }
    const group = byBase.get(baseKey);
    const langNorm = normalizeTemplateLanguage(t.language);
    group.codes.push({ template_code: t.template_code, language: langNorm });
    if (langNorm === 'EN') {
      group.displayName = t.display_name || group.displayName;
    }
  }
  return Array.from(byBase.values()).sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
}

/** Remove trailing "(EN)", "(Spanish)", etc. from template display names for grouped dropdowns. */
export function stripParentheticalLanguageSuffix(label) {
  return String(label || '')
    .replace(/\s*\(\s*(EN|ES|SPA|English|Spanish)\s*\)\s*$/i, '')
    .trim();
}

function languageRowToLetterCode(lang) {
  if (!lang) return null;
  const code = (lang.code || '').toUpperCase().trim();
  const desc = (lang.description || '').toLowerCase();
  if (code === 'ES' || desc === 'spanish') return 'ES';
  if (code === 'EN' || desc === 'english') return 'EN';
  if (code === 'SPA') return 'ES';
  return null;
}

function langIdToLetterCode(id, languages) {
  if (id == null || id === '') return null;
  const lang = (languages || []).find((l) => String(l.id) === String(id));
  return languageRowToLetterCode(lang);
}

/**
 * EN/ES for merged client letters and monthly contact send, using client + primary contact language ids.
 * @param {object} client — row with primary_language_id, preferred_language_id; optional contacts[] with contact.primary_language_id
 * @param {Array<{ id: unknown, code?: string, description?: string }>} languages
 */
export function resolveLetterTemplateLangCodeFromClientRow(client, languages) {
  if (!client) return 'EN';
  const primaryContact = (client.contacts || []).find((c) => c.is_primary);
  const contact = primaryContact?.contact;
  const ids = [
    client.primary_language_id,
    client.preferred_language_id,
    contact?.primary_language_id,
    contact?.preferred_language_id,
  ];
  for (const raw of ids) {
    const code = langIdToLetterCode(raw, languages);
    if (code === 'EN' || code === 'ES') return code;
  }
  return 'EN';
}

/**
 * @param {Array<{ id?: string, template_code?: string, language?: string }>} templates
 * @param {string} baseCodeFromGroup — group.baseCode from groupClientLetterTemplates
 * @param {'EN'|'ES'} wantLang
 */
export function findTemplateIdByBaseAndLanguage(templates, baseCodeFromGroup, wantLang) {
  const targetBase = normalizeTemplateBaseCode(baseCodeFromGroup);
  const row = (templates || []).find(
    (t) =>
      normalizeTemplateBaseCode(t.template_code || '') === targetBase &&
      normalizeTemplateLanguage(t.language) === wantLang
  );
  return row?.id != null ? String(row.id) : null;
}
